import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MdDelete } from 'react-icons/md';
import { RxDragHandleDots2 } from 'react-icons/rx';

function SortableItem({ stage, index, handleUpdate, handleDelete, handleUpdateIsIncidence }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: stage.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div
      key={index}
      ref={setNodeRef}
      style={style}
      className='h-14 w-full flex flex-row items-center bg-[#707070] rounded-lg px-5 mb-2'
    >
      <div className='w-1/12 flex justify-center text-white cursor-move' {...listeners} {...attributes}>
        <RxDragHandleDots2 className="text-xl" />
      </div>
      <div className='w-1/12'>
        <div className='h-10 w-10 bg-[#e5e5e5] text-[#757575] rounded-lg flex items-center justify-center font-bold'>
          {index + 1}
        </div>
      </div>
      <div className='w-8/12 lg:w-9/12'>
        <input
          value={stage.name}
          onChange={({ target }) => {
            handleUpdate(stage, target.value);
          }}
          maxLength={50}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          type='text'
          className='h-10 w-full flex items-center justify-start truncate font-bold bg-[#e5e5e5] text-[#757575] rounded-lg px-3'
        />
      </div>
      <div className="w-1/5 lg:w-1/12 pl-14 flex flex-col text-gray-700 font-semibold">
        <input
          type="checkbox"
          className='ml-2 w-[100%] h-8 cursor-pointer'
          checked={stage?.isIncident}
          onChange={({ target }) => {
            handleUpdateIsIncidence(stage, target.checked);
          }}
        />
      </div>
      <div className='w-1/12 flex justify-end'>
        <div
          onClick={() => handleDelete(stage)}
          className='h-10 w-10 flex justify-center items-center rounded-full bg-[#e5e5e5] text-[#757575] cursor-pointer hover:text-white'
        >
          <MdDelete />
        </div>
      </div>
    </div>
  );
}

function StageList({ localStages, handleUpdate, handleDelete, handleChangeOrder, handleUpdateIsIncidence }) {
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = localStages.findIndex((item) => item.id === active.id);
    const newIndex = localStages.findIndex((item) => item.id === over.id);

    const newStages = arrayMove(localStages, oldIndex, newIndex).map((item, idx) => ({
      ...item,
      order: idx + 1
    }));

    handleChangeOrder(newStages);
  };

  const visibleStages = localStages.filter((item) => item.show);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={visibleStages.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        {visibleStages.map((stage, index) => (
          <SortableItem
            key={stage.id}
            stage={stage}
            index={index}
            handleUpdate={handleUpdate}
            handleDelete={handleDelete}
            handleUpdateIsIncidence={handleUpdateIsIncidence}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}

export default StageList;
